import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { z } from 'zod';
import { format, parseISO, addDays } from 'date-fns';
import { useForm } from 'react-hook-form';
import { OnboardingFormStepLayout } from '@features/onboarding/layouts/OnboardingFormStepLayout';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormStepProps } from '@features/onboarding/interfaces/FormStepProps.interface';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import {
  healthQuestionsSchemaWithConditional,
  HealthQuestionsSchema,
  OnboardingSchema,
} from '@features/onboarding/schema/onboarding';

import { Form } from '@components/ui/form';

import { useFinalSubmissionStore } from 'src/store/final-submission';
import { transformApplicationData } from '@features/onboarding/utils/transformApplicationData';
import { useInsuranceStore } from 'src/store/insurance';
import { Medication } from 'src/services/interfaces/application';
import { useFormControls } from '@features/onboarding/hooks/useFormControls';
import { useHealthStore } from 'src/store/health';
// import { transformApplicationToOnboardingData } from '@features/onboarding/utils/transformApplicationToOnboardingData';
// import { Application } from 'src/services/interfaces/application';
// import { onboardingSchema } from '@features/onboarding/schema/onboarding';

// import { MedicalConditionsIntroStep } from './components/medical-conditions-intro/MedicalConditionsIntroStep';
import { HealthConditionsSliderStep } from './components/health-conditions-slider/HealthConditionsSliderStep';
import { CoverageStartDateStep } from './components/coverage-start-date/CoverageStartDateStep';
import { PrivacyDisclosureStep } from './components/privacy-disclosure/PrivacyDisclosureStep';

import { getStepText } from './stepText';
import {
  CONDITION_DESCRIPTION_ERROR,
  CONDITION_DESCRIPTION_VALIDATION_ERROR,
  HEALTH_QUESTIONS,
  REQUIRE_DETAILS,
} from './constants';
import { TakingMedicationsNowStep } from './components/medication-question/TakingMedicationsNowStep';
import { getHealthStepSchemaFromText } from './schema/phq';
import { InitialQuestionsStep } from './components/initial-questions/InitialQuestionsStep';
import { RiveSignatureAnimation } from './components/privacy-disclosure/RiveAnimation';
import { useApplicationStore } from 'src/store/application';
import { useAIStore } from 'src/store/ai';
import { useGeneralStore } from 'src/store';
import { applicationService } from 'src/services/application';
import { HIDE_DETAILS_FOR } from '@features/onboarding/components/6-health-question-form/constants';
import { FormStepSections } from '@features/onboarding/enums/formSections.enum';
import { motion, AnimatePresence, cubicBezier, useReducedMotion } from 'framer-motion';
import { handleError, ErrorType } from '@utils/errorHandling';
import {
  getGoogleTrackingKey,
  hasGoogleTracking,
  pushGoogleFormPageUpdate,
} from 'src/hooks/useBranding';
import { useW2Mode, useIsW2Mode } from '@features/w2/store/w2-mode';
import { w2StepText } from '@features/w2/copy';

const EASE = cubicBezier(0.22, 1, 0.36, 1);

const PHQ_FORM_SCREEN_TRACKING: Record<
  string,
  { screenPath: string; screenName: string; pageTitle?: string }
> = {
  initialQuestions: {
    screenPath: 'phq/treatment',
    screenName: 'PHQ: Treatment',
    pageTitle: 'Form: PHQ Treatment',
  },
  takingMedicationsNow: {
    screenPath: 'phq/medications',
    screenName: 'PHQ: Medications',
    pageTitle: 'Form: PHQ Medications',
  },
  surgeriesDiabetesHeartMental: {
    screenPath: 'phq/issues',
    screenName: 'PHQ: Issues',
    pageTitle: 'Form: PHQ Issues',
  },
  medDetails: {
    screenPath: 'phq/issues/details',
    screenName: 'PHQ: Issue Details',
    pageTitle: 'Form: PHQ Issue Details',
  },
  otherHealthConditions: {
    screenPath: 'phq/conditions',
    screenName: 'PHQ: Conditions',
    pageTitle: 'Form: PHQ Conditions',
  },
  finalHealthConditions: {
    screenPath: 'phq/conditions/final',
    screenName: 'PHQ: Final Conditions',
    pageTitle: 'Form: PHQ Final Conditions',
  },
  coverageStartDate: {
    screenPath: 'coverage-start-date',
    screenName: 'Coverage Start Date',
    pageTitle: 'Form: Coverage Start Date',
  },
  privacyDisclosure: {
    screenPath: 'phq/disclaimer',
    screenName: 'PHQ: Disclaimer',
    pageTitle: 'Form: PHQ Disclaimer',
  },
  congratulations: {
    screenPath: 'phq/complete',
    screenName: 'PHQ: Complete',
    pageTitle: 'Form: PHQ Complete',
  },
};

const usePageTransitionVariants = (delay = 0) => {
  const shouldReduceMotion = useReducedMotion();

  return {
    initial: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20,
      filter: shouldReduceMotion ? 'none' : 'blur(4px)',
    },
    animate: {
      opacity: 1,
      y: 0,
      filter: 'none',
      transition: {
        duration: 0.6,
        ease: EASE,
        delay,
      },
    },
    exit: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : -10,
      filter: shouldReduceMotion ? 'none' : 'blur(2px)',
      transition: {
        duration: 0.4,
        ease: EASE,
      },
    },
  };
};


export const HealthQuestionForm = ({ onSubmit, onInvalid, defaultStep = 0 }: FormStepProps) => {
  // const filteredHealthQuestions = defaultStep > 0 ? HEALTH_QUESTIONS.filter((_, index) => index === defaultStep) : HEALTH_QUESTIONS.filter((_) => !_.skip)
  // Ref to store timeout IDs for cleanup
  const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const riveBufferRef = useRef<ArrayBuffer | null>(null);
  /**
   * 🔀 Handles custom step skipping logic and synchronizes slider tab for onboarding steps.
   * Call this before default step navigation. Returns true if a custom switch was performed.
   * @param direction 'next' or 'prev' - direction of navigation
   */
  function switchStepWithSliderSync({
    direction,
    currentStep,
    form,
    filteredHealthQuestions,
    setCurrentStep,
  }: {
    direction: 'next' | 'prev';
    currentStep: number;
    form: any;
    filteredHealthQuestions: any[];
    setCurrentStep: (n: number) => void;
  }): boolean {
    // Get step indexes
    const idxSurgeriesDiabetesHeartMental = filteredHealthQuestions.findIndex(
      (q) => q.id === 'surgeriesDiabetesHeartMental'
    );
    const idxMedDetails = filteredHealthQuestions.findIndex(
      (q) => q.id === 'medDetails'
    );
    const idxOtherHealthConditions = filteredHealthQuestions.findIndex(
      (q) => q.id === 'otherHealthConditions'
    );
    const idxCoverageStart = filteredHealthQuestions.findIndex(
      (q) => q.id === 'coverageStartDate'
    );

    const idxPrivacyDisclosure = filteredHealthQuestions.findIndex(
      (q) => q.id === 'privacyDisclosure'
    );

    // Get form values
    const initialQ1 = form.getValues(
      'healthQuestions.initialQuestions.treatment'
    );
    const selectedIssues =
      form.getValues('healthQuestions.surgeriesDiabetesHeartMental.conditions') ||
      [];
    const selectedConditions =
      form.getValues('healthQuestions.medDetails.medications') || [];

    // NEXT direction logic
    if (direction === 'next') {
      if (
        currentStep === idxSurgeriesDiabetesHeartMental &&
        selectedIssues[0]?.value === 'none' &&
        initialQ1 === false
      ) {
        const targetIdx = defaultStep > 0 && idxCoverageStart >= 0
          ? idxCoverageStart
          : idxPrivacyDisclosure;
        setCurrentStep(targetIdx);
        return true;
      }
      if (
        selectedIssues[0]?.value !== 'none' &&
        initialQ1 === false &&
        currentStep === idxMedDetails
      ) {
        if (!selectedConditions.length) {
          return false;
        }
        setCurrentStep(idxCoverageStart >= 0 ? idxCoverageStart : idxPrivacyDisclosure);
        return true;
      }
      if (
        currentStep === idxSurgeriesDiabetesHeartMental &&
        initialQ1 === true &&
        selectedIssues[0]?.value === 'none'
      ) {
        setCurrentStep(idxOtherHealthConditions);
        return true;
      }
    }
    // PREV direction logic
    if (direction === 'prev') {
      if (
        currentStep === idxPrivacyDisclosure &&
        initialQ1 === false &&
        selectedIssues[0]?.value === 'none'
      ) {
        const targetIdx = defaultStep > 0 && idxCoverageStart >= 0
          ? idxCoverageStart
          : idxSurgeriesDiabetesHeartMental;
        setCurrentStep(targetIdx);
        return true;
      }
      if (
        currentStep === idxCoverageStart &&
        initialQ1 === false &&
        selectedIssues[0]?.value === 'none'
      ) {
        setCurrentStep(idxSurgeriesDiabetesHeartMental);
        return true;
      }
      if (
        currentStep === idxCoverageStart &&
        initialQ1 === false &&
        selectedIssues[0]?.value !== 'none'
      ) {
        setCurrentStep(idxMedDetails);
        return true;
      }
      if (
        currentStep === idxOtherHealthConditions &&
        initialQ1 === true &&
        selectedIssues[0]?.value === 'none'
      ) {
        setCurrentStep(idxSurgeriesDiabetesHeartMental);
        return true;
      }
    }
    return false;
  }

  const {
    data,
    isLastSelectedPHQ,
    setData,
    setLastSelectedPHQ,
    setPhqSubmitted,
    registerForm,
    unregisterForm,
  } = useOnboardingStore();
  const { handleJumpTo, handleNextStep: handleNextStepGlobal } = useFormControls();
  const { issues, conditions, issueDetails, setFailValidation } =
    useHealthStore();
  const [showAnimationScreen, setShowAnimationScreen] = useState(false);
  const currentApplicationId = useApplicationStore((state) => state.currentApplicationId);
  const tracking = useGeneralStore((state) => state.siteConfig?.tracking);
  const trackingKey = getGoogleTrackingKey(tracking);
  const trackedPhqScreenRef = useRef<string | null>(null);

  // Move the hook call to the top level
  const pageTransitionVariants = usePageTransitionVariants();
  const {
    isUnderwritingLoading,
    underwritingResult,
    underwritingError,
    isSubmittingApplication,
    runUnderwriting,
    getEstimation,
  } = useFinalSubmissionStore();
  const plans = useInsuranceStore((state) => state.plans);
  const shouldClearPregnancyOnNextPHQ =
    (data as Record<string, unknown>).clearPregnancyOnNextPHQ === true;
  // The electronic signature is never pre-filled from the applicant's name: the
  // signer must type it themselves.
  const getPrivacySignature = useCallback(
    (signature?: string) => (signature?.trim() ? signature : ''),
    []
  );

  const form = useForm<HealthQuestionsSchema>({
    resolver: zodResolver(healthQuestionsSchemaWithConditional),
    defaultValues: {
      ...data,
      healthQuestions: {
        initialQuestions: {
          treatment:
            data.healthQuestions?.initialQuestions?.treatment ?? undefined,
          invalid: data.healthQuestions?.initialQuestions?.invalid ?? undefined,
          pregnancy:
            shouldClearPregnancyOnNextPHQ
              ? undefined
              : data.healthQuestions?.initialQuestions?.pregnancy ?? undefined,
        },
        takingMedicationsNow: {
          value: data.healthQuestions?.takingMedicationsNow?.value ?? undefined,
          medications:
            data.healthQuestions?.takingMedicationsNow?.medications ?? [],
        },
        surgeriesDiabetesHeartMental: {
          conditions:
            data.healthQuestions?.surgeriesDiabetesHeartMental?.conditions ??
            [],
          details:
            data.healthQuestions?.surgeriesDiabetesHeartMental?.details ?? {},
        },
        medDetails: {
          medications: data.healthQuestions?.medDetails?.medications ?? [],
          details: data.healthQuestions?.medDetails?.details ?? {},
        },
        otherHealthConditions: {
          conditions:
            data.healthQuestions?.otherHealthConditions?.conditions ?? [],
          details: data.healthQuestions?.otherHealthConditions?.details ?? {},
        },
        finalHealthConditions: {
          value:
            data.healthQuestions?.finalHealthConditions?.value ?? undefined,
        },
        coverageStartDate: {
          effectiveDate:
            data.healthQuestions?.coverageStartDate?.effectiveDate ?? undefined,
        },
        privacyDisclosure: {
          disclaimer:
            data.healthQuestions?.privacyDisclosure?.disclaimer ?? false,
          signature: getPrivacySignature(
            data.healthQuestions?.privacyDisclosure?.signature
          ),
          accepted: data.healthQuestions?.privacyDisclosure?.accepted ?? false,
        },
      },
    },
    mode: 'onChange',
  });

  const isTreatmentYes = form.watch('healthQuestions.initialQuestions.treatment') === true;
  const filteredHealthQuestions = useMemo(() => {
    const base = defaultStep > 0
      ? HEALTH_QUESTIONS.filter((_, index) => index === defaultStep)
      : HEALTH_QUESTIONS.filter((q) => !q.skip);
    if (defaultStep > 0) return base;
    if (!isTreatmentYes) {
      return base.filter((q) => q.id !== 'otherHealthConditions');
    }
    return base;
  }, [defaultStep, isTreatmentYes]);

  const idxPrivacyDisclosure = filteredHealthQuestions.findIndex(
    (q) => q.id === 'privacyDisclosure'
  );

  const [currentStep, setCurrentStep] = useState(0);
  const [stepErrorMessage, setStepErrorMessage] = useState<string | null>(null);
  useEffect(() => {
    if (filteredHealthQuestions.length === 0) {
      return;
    }
    setCurrentStep((prev) => {
      if (prev < 0) return 0;
      const maxIndex = filteredHealthQuestions.length - 1;
      return prev > maxIndex ? maxIndex : prev;
    });
  }, [filteredHealthQuestions, filteredHealthQuestions.length]);

  const currentStepQuestion = filteredHealthQuestions[currentStep];

  useEffect(() => {
    const screenId = defaultStep > 0 ? 'coverageStartDate' : currentStepQuestion?.id;
    const screen = screenId ? PHQ_FORM_SCREEN_TRACKING[screenId] : undefined;
    if (!screen || !hasGoogleTracking(tracking)) return;

    const screenKey = `${trackingKey}:${screen.screenPath}`;
    if (trackedPhqScreenRef.current === screenKey) return;

    pushGoogleFormPageUpdate(tracking, screen);
    trackedPhqScreenRef.current = screenKey;
  }, [currentStepQuestion?.id, defaultStep, tracking, trackingKey]);

  useEffect(() => {
    if (!currentStepQuestion?.id) return;
    useAIStore.getState().setCurrentPage(`phq-${currentStepQuestion.id}`);
  }, [currentStepQuestion?.id]);

  useEffect(() => {
    if (!shouldClearPregnancyOnNextPHQ) return;

    form.setValue('healthQuestions.initialQuestions.pregnancy' as any, undefined, {
      shouldDirty: false,
      shouldValidate: false,
    });
    useOnboardingStore.setState((state) => ({
      data: {
        ...state.data,
        clearPregnancyOnNextPHQ: false,
        healthQuestions: {
          ...state.data.healthQuestions,
          initialQuestions: {
            ...state.data.healthQuestions?.initialQuestions,
            pregnancy: undefined,
          },
        },
      } as typeof state.data,
    }));
  }, [form, shouldClearPregnancyOnNextPHQ]);


  const questionId = filteredHealthQuestions[currentStep]?.id;
  // W2 mode swaps the PHQ sub-step copy (spec screens 6–9) via a code-side
  // override; any id not overridden falls back to the shared B2C copy.
  const isW2StepText = useIsW2Mode();
  const { title, description } =
    (isW2StepText && w2StepText(questionId)) || getStepText(questionId);

  useEffect(() => {
    if (isLastSelectedPHQ) {
      console.log('isLastSelectedPHQ', isLastSelectedPHQ);
      // setActiveCategoryIndex(planCategories.length - 1);
      setCurrentStep(idxPrivacyDisclosure);
      setLastSelectedPHQ(false);
    }
  }, [isLastSelectedPHQ, setCurrentStep, setLastSelectedPHQ, idxPrivacyDisclosure]);

  useEffect(() => {
    registerForm('healthQuestionForm', (data) => {
      form.reset(data);
    });

    return () => {
      unregisterForm('healthQuestionForm');
    };
  }, [form, registerForm, unregisterForm]);

  useEffect(() => {
    const subscription = form.watch((watchValues) => {
      const watchedValues = watchValues as Partial<HealthQuestionsSchema>;
      const createDefaultHealthQuestions = (): OnboardingSchema['healthQuestions'] => ({
        initialQuestions: {
          treatment: false,
          invalid: false,
          pregnancy: undefined,
        },
        takingMedicationsNow: {
          value: false,
          medications: [],
        },
        surgeriesDiabetesHeartMental: {
          conditions: [],
          details: {},
        },
        medDetails: {
          medications: [],
          details: {},
        },
        otherHealthConditions: {
          conditions: [],
          details: {},
        },
        finalHealthConditions: {
          value: undefined,
        },
        coverageStartDate: {
          effectiveDate: undefined,
        },
        privacyDisclosure: {
          disclaimer: false,
          accepted: false,
          signature: '',
        },
      });

      const mergeHealthQuestions = (
        target: OnboardingSchema['healthQuestions'],
        source?: Partial<OnboardingSchema['healthQuestions']>
      ) => {
        if (!source) return target;
        (Object.keys(source) as Array<keyof OnboardingSchema['healthQuestions']>).forEach((key) => {
          const value = source[key];
          if (value !== undefined) {
            (target as Record<string, unknown>)[key as string] = value as unknown;
          }
        });
        return target;
      };

      const mergedHealthQuestions = mergeHealthQuestions(
        mergeHealthQuestions(
          createDefaultHealthQuestions(),
          data.healthQuestions as Partial<OnboardingSchema['healthQuestions']> | undefined
        ),
        watchedValues.healthQuestions as Partial<OnboardingSchema['healthQuestions']> | undefined
      );

      const mergedData: Partial<OnboardingSchema> = {
        ...data,
        healthQuestions: mergedHealthQuestions,
      };

      const transformedApplication = transformApplicationData(mergedData, plans);

      useApplicationStore.setState((state) => {
        const existingApplications = state.applications ?? [];
        const currentApplication = existingApplications[0];

        const mergedApplication = currentApplication
          ? {
              ...currentApplication,
              ...transformedApplication,
              id: currentApplication.id,
            }
          : transformedApplication;

        const isSame =
          currentApplication &&
          JSON.stringify(currentApplication) === JSON.stringify(mergedApplication);

        if (isSame) {
          return {};
        }

        const nextApplications =
          existingApplications.length > 0
            ? [mergedApplication, ...existingApplications.slice(1)]
            : [mergedApplication];

        return {
          applications: nextApplications,
        };
      });
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [form, data, plans]);

  useEffect(() => {
    setStepErrorMessage(null);
    setFailValidation(false);
    
    // Clear any previous estimation errors when returning to health questions
    if (currentStep === 0) {
      useFinalSubmissionStore.setState({
        underwritingResult: null,
        underwritingError: null,
        isUnderwritingLoading: false,
      });
    }
  }, [currentStep]);

  useEffect(() => {
    const preloadRiveAnimation = async () => {
      try {
        const response = await fetch('/anims/loader2.riv');
        const buffer = await response.arrayBuffer();
        riveBufferRef.current = buffer;
      } catch (error) {
        console.error('Failed to preload RIV animation:', error);
      }
    };

    preloadRiveAnimation();
  }, []);

  useEffect(() => {
    useApplicationStore.getState().updateTierFromApplicationState();
  }, []);

  // Cleanup timeout on unmount to prevent navigation after component is unmounted
  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        console.log('[HealthQuestionForm] Clearing navigation timeout on unmount');
        clearTimeout(navigationTimeoutRef.current);
        navigationTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (data.healthQuestions && Object.keys(data.healthQuestions).length > 0) {
      const formData = {
        ...data,
        healthQuestions: {
          initialQuestions: {
            treatment:
              data.healthQuestions.initialQuestions?.treatment ?? undefined,
            invalid:
              data.healthQuestions.initialQuestions?.invalid ?? undefined,
            pregnancy:
              shouldClearPregnancyOnNextPHQ
                ? undefined
                : data.healthQuestions.initialQuestions?.pregnancy ?? undefined,
          },
          takingMedicationsNow: {
            value:
              data.healthQuestions.takingMedicationsNow?.value ?? undefined,
            medications:
              data.healthQuestions.takingMedicationsNow?.medications ?? [],
          },
          surgeriesDiabetesHeartMental: {
            conditions:
              data.healthQuestions.surgeriesDiabetesHeartMental?.conditions ??
              [],
            details:
              data.healthQuestions.surgeriesDiabetesHeartMental?.details ?? {},
          },
          medDetails: {
            medications: data.healthQuestions.medDetails?.medications ?? [],
            details: data.healthQuestions.medDetails?.details ?? {},
          },
          otherHealthConditions: {
            conditions:
              data.healthQuestions.otherHealthConditions?.conditions ?? [],
            details: data.healthQuestions.otherHealthConditions?.details ?? {},
          },
          finalHealthConditions: {
            value:
              data.healthQuestions.finalHealthConditions?.value ?? undefined,
          },
          coverageStartDate: {
            effectiveDate:
              data.healthQuestions.coverageStartDate?.effectiveDate ??
              undefined,
          },
          privacyDisclosure: {
            disclaimer:
              data.healthQuestions.privacyDisclosure?.disclaimer ?? false,
            signature: getPrivacySignature(
              data.healthQuestions.privacyDisclosure?.signature
            ),
            accepted: data.healthQuestions.privacyDisclosure?.accepted ?? false,
          },
        },
      };

      const currentFormData = form.getValues();
      const currentSignature =
        currentFormData.healthQuestions?.privacyDisclosure?.signature;
      const newSignature =
        formData.healthQuestions?.privacyDisclosure?.signature;

      const signatureChanged = currentSignature !== newSignature;
      const otherDataChanged =
        JSON.stringify({
          ...currentFormData,
          healthQuestions: {
            ...currentFormData.healthQuestions,
            privacyDisclosure: {
              ...currentFormData.healthQuestions?.privacyDisclosure,
              signature: currentSignature,
            },
          },
        }) !==
        JSON.stringify({
          ...formData,
          healthQuestions: {
            ...formData.healthQuestions,
            privacyDisclosure: {
              ...formData.healthQuestions?.privacyDisclosure,
              signature: newSignature,
            },
          },
        });

      if (signatureChanged || otherDataChanged) {
        form.reset(formData);
      }
    }
  }, [data, getPrivacySignature]);

  const isCurrentStepValid = (currentStep: number, formData: any): boolean => {
    const question = filteredHealthQuestions[currentStep];
    if (!question?.id) return false;

    const questionId = question.id;

    const stepSchema = getHealthStepSchemaFromText(questionId);
    if (!stepSchema) {
      setStepErrorMessage(null);
      return true;
    }

    const stepData = {
      healthQuestions: {
        [questionId]: formData.getValues(`healthQuestions.${questionId}`),
      },
    };

    try {
      stepSchema.parse(stepData);
      setStepErrorMessage(null);
      return true;
    } catch (err) {
      if (err instanceof z.ZodError) {
        setStepErrorMessage(
          err.errors[0]?.message || 'Please complete the current step.'
        );
      } else {
        setStepErrorMessage('Please complete the current step.');
      }
      return false;
    }
  };

  const handleNext = () => {
    if (defaultStep > 0) {
      console.log('its end')
      handleJumpTo('Finish Up');
      // setLastSelectedPHQ(true);
      return;
    }

    let nextIndex = currentStep + 1;
    // console.log(filteredHealthQuestions[nextIndex])
    const nextQuestion = filteredHealthQuestions[nextIndex]?.id;
    const nextQuestionSkip = filteredHealthQuestions[nextIndex]?.skip;
    // console.log(filteredHealthQuestions)
    if (nextQuestionSkip) {
      nextIndex = nextIndex + 1;
    }

    if (nextQuestion === 'currentMeds') {
      setCurrentStep(nextIndex + 1);
      return;
    }

    if (currentStep < filteredHealthQuestions.length - 1) {
      setCurrentStep(nextIndex);
    } else {
      // PHQ завершен - переходим на следующий шаг в общем flow (EstimateResult)
      console.log('[HealthQuestionForm] PHQ completed, navigating to next step in global flow');
      handleNextStepGlobal({});
      handleJumpTo(FormStepSections.EstimateResult);
    }
  };

  const handlePrevious = () => {
    const prevIndex = currentStep - 1;

    const currentId = filteredHealthQuestions[currentStep]?.id;
    const prevQuestion = filteredHealthQuestions[prevIndex]?.id;

    // if (
    //   currentId === 'medicalConditionsIntro' &&
    //   prevQuestion === 'currentMeds'
    // ) {
    //   setCurrentStep(prevIndex - 1);
    //   return;
    // }

    if (currentStep > 0) {
      setCurrentStep(prevIndex);
    }
  };

  const isBlankDescription = (value: unknown) =>
    typeof value !== 'string' || value.trim() === '';

  const isType2MedIssueValue = (value: string) => {
    const [, detailKey] = value.split('-', 2);
    if (!detailKey) return false;
    return issueDetails.some(
      (detail) => detail.key === detailKey && detail.type === 2
    );
  };

  const hasMissingMedIssueDescription = (
    fields: Array<{ value?: string; details?: string }>
  ) => {
    const details =
      form.getValues('healthQuestions.medDetails.details') ?? {};

    return fields.some((field) => {
      const value = field.value;
      if (
        !value ||
        !value.includes('-') ||
        value.endsWith('-none') ||
        isType2MedIssueValue(value)
      ) {
        return false;
      }

      const rawDescription =
        typeof details[value] === 'string' ? details[value] : field.details;
      return isBlankDescription(rawDescription);
    });
  };

  const validationOtherHealthConditions1 = () => {
    const activeTabs =
      form.getValues(
        'healthQuestions.surgeriesDiabetesHeartMental.conditions'
      ) ?? [];
    const fields =
      form.getValues('healthQuestions.medDetails.medications') ?? [];

    const selectedAllElems = activeTabs.filter(e => e.value !== 'none').map(e => e.value);
    const selectedGroups = selectedAllElems.filter(e => HIDE_DETAILS_FOR.includes(e));

    if (selectedGroups.length === 0) {
      return false;
    }

    const fieldsByGroup: Record<string, any[]> = {};
    fields.forEach((field) => {
      const value = typeof field === 'object' ? field?.value : field;
      if (value && String(value).includes('-')) {
        const [groupName] = String(value).split('-');
        if (groupName && selectedGroups.includes(groupName)) {
          if (!fieldsByGroup[groupName]) {
            fieldsByGroup[groupName] = [];
          }
          fieldsByGroup[groupName].push(field);
        }
      }
    });

    for (const group of selectedGroups) {
      const groupFields = fieldsByGroup[group] || [];
      if (groupFields.length === 0) {
        return true;
      }
    }

    return hasMissingMedIssueDescription(fields);
  };
  const validationTakingMedicationsNow = () => {
    const isTakingMedications = form.getValues('healthQuestions.takingMedicationsNow.value');

    // If user selected "NO", skip medication validation entirely
    if (isTakingMedications === false) {
      return false;
    }

    // @ts-ignore
    const rawMedications = (form.getValues('healthQuestions.takingMedicationsNow.medications') as Medication[]) ?? [];
    // Filter out empty/ghost entries that can remain after remove()
    const fieldsMedications = rawMedications.filter((m: any) => m?.id);
    const hasInvalidFields = fieldsMedications?.some((field) => {
      // Check core fields: applicant, dosage (dosageId or dosageDescription), frequency
      // If dosageId is '__OTHER__' or not set, dosageDescription is required
      const isOtherDosage = field.dosageId === '__OTHER__';
      // Check for empty string, falsy value, or strings containing only whitespace
      const hasDosageDescription = field.dosageDescription && field.dosageDescription.trim().length > 0;
      const hasDosageId = field.dosageId && field.dosageId !== '__OTHER__' && field.dosageId !== undefined && field.dosageId !== null && field.dosageId !== '';
      
      // Dosage is valid if: has valid dosageId OR (isOtherDosage/empty dosageId AND has dosageDescription)
      // Also check if dosageDescription is not just whitespace
      const hasDosage = hasDosageId || ((isOtherDosage || !field.dosageId) && hasDosageDescription);
      const missingCore = !field.applicant || !hasDosage || !field.frequency;
      
      // Check frequencyDescription for "Every X hours" or "As needed"
      // Check for empty string, falsy value, or strings containing only whitespace
      const needsFrequencyDescription = field.frequency === 'Every X hours' || field.frequency === 'As needed';
      const hasFrequencyDescription = field.frequencyDescription && field.frequencyDescription.trim().length > 0;
      const missingFrequencyDescription = needsFrequencyDescription && !hasFrequencyDescription;
      
      // Check description - also check for whitespace-only strings
      const hasDescription = field.description && field.description.trim().length > 0;
      const missingDesc = REQUIRE_DETAILS && !hasDescription;
      return missingCore || missingFrequencyDescription || missingDesc;
    });
    if (isTakingMedications && !fieldsMedications.length) {
      console.log('[PHQ] validationTakingMedicationsNow — FAIL: no medications added');
      setFailValidation(true);
      return true;
    }
    if (hasInvalidFields) {
      console.log('[PHQ] validationTakingMedicationsNow — FAIL: some medications have invalid fields');
    }
    return hasInvalidFields;
  };

  const localValidation = (): {
    isInvalid: boolean;
    didSwitchStep: boolean;
  } => {
    const medDetails = filteredHealthQuestions.findIndex((q) => q.id === 'medDetails');
    const idxTakingMedicationsNow = filteredHealthQuestions.findIndex(
      (q) => q.id === 'takingMedicationsNow'
    );
    const idxOtherHealthConditions = filteredHealthQuestions.findIndex(
      (q) => q.id === 'otherHealthConditions'
    );

    // Always validate medications when user takes them - prevents update with invalid data when navigating between PHQ steps (e.g. Back then Next)
    const isInvalidTakingMedications = validationTakingMedicationsNow();
    const isInvalidOtherHealthConditions =
      currentStep === medDetails ? validationOtherHealthConditions1() : false;
    const isStepInvalid = !isCurrentStepValid(currentStep, form);
    const isInvalidDetails =
      currentStep === idxOtherHealthConditions ? validateDetails() : false;

    const isInvalid =
      isInvalidTakingMedications ||
      isInvalidOtherHealthConditions ||
      isStepInvalid ||
      isInvalidDetails;

    if (isInvalid) {
      return {
        isInvalid: true,
        didSwitchStep: false,
      };
    }

    const didSwitchStep = switchStepWithSliderSync({
      direction: 'next',
      currentStep,
      form,
      filteredHealthQuestions,
      setCurrentStep,
    });

    // console.log('isNotValid', isNotValid);

    return {
      isInvalid: false,
      didSwitchStep,
    };
  };

  const validateDetails = () => {
    const conditions =
      form.getValues('healthQuestions.otherHealthConditions.conditions') ?? [];
    const details =
      form.getValues('healthQuestions.otherHealthConditions.details') ?? {};

    for (const condition of conditions) {
      const value = condition.value;
      if (!HIDE_DETAILS_FOR.includes(value) && value !== 'none') {
        const detailValue = details[value];
        if (isBlankDescription(detailValue)) {
          return true;
        }
      }
    }
    return false;
  };

  const getFirstInvalidConditionStep = () => {
    const idxMedDetails = filteredHealthQuestions.findIndex(
      (q) => q.id === 'medDetails'
    );
    if (idxMedDetails >= 0 && validationOtherHealthConditions1()) {
      return idxMedDetails;
    }

    const idxOtherHealthConditions = filteredHealthQuestions.findIndex(
      (q) => q.id === 'otherHealthConditions'
    );
    if (idxOtherHealthConditions >= 0 && validateDetails()) {
      return idxOtherHealthConditions;
    }

    return -1;
  };

  const MEDICATION_VALIDATION_ERROR = 'MEDICATION_VALIDATION_FAILED';

  const onNextClick = async () => {
    console.log('[PHQ] onNextClick called, currentStep:', currentStep, 'stepId:', filteredHealthQuestions[currentStep]?.id);

    const { isInvalid, didSwitchStep } = localValidation();
    console.log('[PHQ] localValidation result:', isInvalid, 'didSwitchStep:', didSwitchStep);

    if (isInvalid) {
      const medValid = validationTakingMedicationsNow();

      setFailValidation(true);
      // Navigate to medications step so user sees validation errors
      if (medValid) {
        const idxTakingMedicationsNow = filteredHealthQuestions.findIndex(
          (q) => q.id === 'takingMedicationsNow'
        );
        if (idxTakingMedicationsNow >= 0) {
          setCurrentStep(idxTakingMedicationsNow);
        }
      }
      const err = new Error(MEDICATION_VALIDATION_ERROR);
      (err as Error & { code: string }).code = MEDICATION_VALIDATION_ERROR;
      throw err;
    }

    setFailValidation(false);

    const formData = form.getValues();

    setData(formData);

    if (didSwitchStep) {
      return;
    }

    if (defaultStep > 0) {
      handleNext();
      return;
    }

    if (filteredHealthQuestions[currentStep]?.id === 'privacyDisclosure') {
      // Validate medications before making API request - do not send if validation fails
      if (validationTakingMedicationsNow()) {
        setFailValidation(true);
        const idxTakingMedicationsNow = filteredHealthQuestions.findIndex(
          (q) => q.id === 'takingMedicationsNow'
        );
        if (idxTakingMedicationsNow >= 0) {
          setCurrentStep(idxTakingMedicationsNow);
        }
        const err = new Error(MEDICATION_VALIDATION_ERROR);
        (err as Error & { code: string }).code = MEDICATION_VALIDATION_ERROR;
        throw err;
      }

      const invalidConditionStep = getFirstInvalidConditionStep();
      if (invalidConditionStep >= 0) {
        setFailValidation(true);
        setStepErrorMessage(CONDITION_DESCRIPTION_ERROR);
        setCurrentStep(invalidConditionStep);
        const err = new Error(CONDITION_DESCRIPTION_VALIDATION_ERROR);
        (err as Error & { code: string }).code =
          CONDITION_DESCRIPTION_VALIDATION_ERROR;
        throw err;
      }

      // W2 fork (ADR-0003/0011): the reused B2C questionnaire terminates here for
      // employees. Persist the FULL B2C payload (the whole onboarding `data` —
      // applicants + householdIncome + healthQuestions) to `employees.phqPayload`
      // via the W2 endpoint. NO estimation / underwriting / AI / plan / payment —
      // that is the only post-questionnaire divergence from B2C.
      if (useW2Mode.getState().active) {
        const fullPayload = {
          ...useOnboardingStore.getState().data,
          version: 'w2-b2c-v1',
        };
        setShowAnimationScreen(true);
        setStepErrorMessage(null);
        try {
          await useW2Mode.getState().completePhq(fullPayload);
          // Only mark the PHQ submitted once the W2 write actually resolved, so a
          // failed submit doesn't leave dirty "submitted" state (finding #2).
          setPhqSubmitted(true);
        } catch (error) {
          // Redacted log only: never emit the raw AxiosError (its config.data is
          // the full PHQ payload — health answers, names, DOB = PHI). Log HTTP
          // status + backend message string, nothing else.
          if (axios.isAxiosError(error)) {
            const status = error.response?.status ?? 'network-error';
            const message =
              typeof error.response?.data?.message === 'string'
                ? error.response.data.message
                : error.message;
            console.error(
              `W2 PHQ submission failed (status: ${status}, message: ${message})`
            );
          } else {
            console.error('W2 PHQ submission failed (non-axios error)');
          }
          setShowAnimationScreen(false);
          setStepErrorMessage(
            'We could not submit your questionnaire. Please try again.'
          );
        }
        return;
      }

      const requestFormData = transformApplicationData(formData, plans);

      setShowAnimationScreen(true);
      setStepErrorMessage(null);

      // Mark PHQ as submitted before calling estimation
      setPhqSubmitted(true);

      // Sync application state to store (phqRejectedAt, phqAcceptedAt, phqUnderReviewAt)
      await useApplicationStore.getState().updateTierFromApplicationState();
      const applicationState = useApplicationStore.getState();
      const { phqAcceptedAt: apiPhqAcceptedAt, phqRejectedAt: apiPhqRejectedAt } = applicationState;

      if (!apiPhqAcceptedAt && !apiPhqRejectedAt) {
        await new Promise(resolve => setTimeout(resolve, 4500));

        try {
          let estimationResult;

          if (currentApplicationId) {
            try {
              await applicationService.updateApplicationById(
                currentApplicationId,
                requestFormData
              );
            } catch (error) {
              console.error('Error updating application:', error);
              handleError(ErrorType.FORM, error);
              setShowAnimationScreen(false);
              return;
            }
            try {
              await getEstimation(currentApplicationId);
              // Get the result from store after estimation
              estimationResult = useFinalSubmissionStore.getState().underwritingResult;
            } catch (error) {
              console.error('Error getting estimation:', error);
              handleError(ErrorType.UNDERWRITING, error);
              setShowAnimationScreen(false);
              return;
            }
          } else {
            try {
              await runUnderwriting(requestFormData);
              // Get the result from store after estimation
              estimationResult = useFinalSubmissionStore.getState().underwritingResult;
            } catch (error) {
              console.error('Error running underwriting:', error);
              handleError(ErrorType.UNDERWRITING, error);
              setShowAnimationScreen(false);
              return;
            }
          }

          // Check if estimation returned a result
          if (!estimationResult) {
            // BUG FIX: Show error if estimation service fails
            setShowAnimationScreen(false);
            handleError(ErrorType.UNDERWRITING);
            setStepErrorMessage('Estimation service is unavailable. Please try again later.');
            return;
          }

          // Handle different estimation statuses
          // Clear any existing timeout
          if (navigationTimeoutRef.current) {
            clearTimeout(navigationTimeoutRef.current);
          }

          // Navigate to EstimateResult step after animation
          navigationTimeoutRef.current = setTimeout(() => {
            setShowAnimationScreen(false);
            console.log('[HealthQuestionForm] Estimation complete, navigating to next step (EstimateResult)');
            handleJumpTo(FormStepSections.EstimateResult);
            navigationTimeoutRef.current = null;
          }, 3000);
        } catch (error) {
          // BUG FIX: Handle API errors properly - navigate to EstimateResult with rejection
          console.error('Error during estimation:', error);
          handleError(ErrorType.UNDERWRITING, error);
          setShowAnimationScreen(false);
          
          // Set rejection result in store to show appropriate UI
          const rejectResult = {
            status: 'rejected' as const,
            details: {
              reason: 'API Error',
              error: 'Estimation service is unavailable. Please try again later.'
            }
          };
          
          // Update the store with rejection result
          useFinalSubmissionStore.setState({
            underwritingResult: rejectResult,
            underwritingError: 'Estimation service is unavailable. Please try again later.',
            isUnderwritingLoading: false,
          });
          
          // Navigate to EstimateResult step to show rejection screen
          console.log('[HealthQuestionForm] Estimation failed, navigating to EstimateResult with rejection status');
          handleJumpTo(FormStepSections.EstimateResult);
          return;
        }
      } else {
        // User already has accepted or rejected PHQ - go to next step (EstimateResult)
        // If rejected, set underwritingResult so EstimateResult shows NoWorriesStep
        if (apiPhqRejectedAt) {
          useApplicationStore.getState().setIsMainPlanRejected(true);
          useOnboardingStore.getState().rejectMedicalPlan();
          useFinalSubmissionStore.setState({
            underwritingResult: {
              status: 'rejected' as const,
              details: { reason: 'Human Review Rejected' },
            },
          });
        }
        // Clear any existing timeout
        if (navigationTimeoutRef.current) {
          clearTimeout(navigationTimeoutRef.current);
        }

        navigationTimeoutRef.current = setTimeout(() => {
          setShowAnimationScreen(false);
          console.log('[HealthQuestionForm] PHQ already accepted/rejected, navigating to next step');
          handleJumpTo(FormStepSections.EstimateResult);
          navigationTimeoutRef.current = null;
        }, 1000);
      }
    } else {
      handleNext();
    }
  };
  const renderAnimationScreen = () => {
    return (
      <div className="w-full h-full flex items-center justify-center bg-transparent">
        <RiveSignatureAnimation
          riveFileUrl="/anims/loader2.riv"
          width={300}
          height={150}
        />
      </div>
    );
  };

  const onPrevClick = async () => {
    setFailValidation(false);
    if (
      switchStepWithSliderSync({
        direction: 'prev',
        currentStep,
        form,
        filteredHealthQuestions,
        setCurrentStep,
      })
    ) {
      return;
    }

    handlePrevious();
  };

  const renderStep = () => {
    const currentQuestion = filteredHealthQuestions[currentStep];
    if (!currentQuestion) {
      return null;
    }

    const question = { ...currentQuestion };
    const errorMessage = stepErrorMessage;

    if (question.id === 'coverageStartDate') {
      const effectiveDate = form.getValues(
        'healthQuestions.coverageStartDate.effectiveDate'
      );
      if (effectiveDate) {
        const options = question.options as { label: string; value: string }[];
        const dateExists = options.some((opt) => opt.value === effectiveDate);

        if (!dateExists) {
          const date = parseISO(effectiveDate);
          const adjustedDate = addDays(date, 1);

          question.options = [
            ...options,
            {
              value: effectiveDate,
              label: format(adjustedDate, 'MMMM d, yyyy'),
            },
          ];
        }
      }
    }

    const stepIds = [
      'surgeriesDiabetesHeartMental',
      'medDetails',
      'otherHealthConditions',
      'finalHealthConditions',
    ];

    let questionsToPass = filteredHealthQuestions.filter((q) =>
      stepIds.includes(q.id)
    );

    // console.log(questionsToPass)

    if (issues.length > 0) {
      questionsToPass = [...questionsToPass];
      const surgeriesIdx = questionsToPass.findIndex(
        (q) => q.id === 'surgeriesDiabetesHeartMental'
      );
      if (surgeriesIdx !== -1) {
        questionsToPass[surgeriesIdx] = {
          ...questionsToPass[surgeriesIdx],
          options: [
            ...issues.map((v) => ({ label: v.label, value: v.key })),
            {
              label:
                "<strong>NONE APPLY</strong> – I've  read and understood the conditions above.",
              value: 'none',
              isNoneApply: true,
            },
          ],
        };
      }
    }
    if (conditions.length > 0) {
      const condIdx = questionsToPass.findIndex(
        (q) => q.id === 'otherHealthConditions'
      );
      if (condIdx !== -1) {
        questionsToPass[condIdx] = {
          ...questionsToPass[condIdx],
          options: [
            ...conditions.map((c) => ({ label: c.label, value: c.key })),
            {
              label:
                "<strong>NONE APPLY</strong> – I've  read and understood the conditions above.",
              value: 'none',
              isNoneApply: true,
            },
          ],
        };
      }
    }
    const detailsIdx = questionsToPass.findIndex((q) => q.id === 'medDetails');
    const selectedIssues =
      form.getValues(
        'healthQuestions.surgeriesDiabetesHeartMental.conditions'
      ) || [];
    if (detailsIdx !== -1 && issues.length > 0 && issueDetails.length > 0) {
      const grouped = issues
        .filter((issue) =>
          selectedIssues.map((v) => v.value).includes(issue.key)
        )
        .map((issue) => [
          ...issueDetails
            .filter((d) => d.issueId === issue.id)
            .map((d) => ({ label: d.label, value: d.key })),
          {
            label:
              "<strong>NONE APPLY</strong> – I've read and understood the conditions above.",
            value: 'none',
            isNoneApply: true,
          },
        ]);
      questionsToPass[detailsIdx] = {
        ...questionsToPass[detailsIdx],
        options: grouped,
      };
    }
    // console.log(questionsToPass)
    const activeTab = questionsToPass.findIndex((e) => e.id === currentQuestion.id);

    switch (question.id) {
      case 'initialQuestions':
        return <InitialQuestionsStep form={form} question={question} />;
      case 'takingMedicationsNow':
        return <TakingMedicationsNowStep form={form} question={question} />;
      // case 'medicalConditionsIntro':
      //   return <MedicalConditionsIntroStep form={form} question={question} />;
      case 'surgeriesDiabetesHeartMental':
      case 'medDetails':
      case 'otherHealthConditions':
      case 'finalHealthConditions':
        return (
          <HealthConditionsSliderStep
            questions={questionsToPass}
            activeTab={activeTab}
            form={form}
            errorMessage={errorMessage}
            setStepErrorMessage={setStepErrorMessage}
          />
        );
      case 'coverageStartDate':
        return (
          <CoverageStartDateStep
            form={form}
            question={question}
            errorMessage={errorMessage}
            setStepErrorMessage={setStepErrorMessage}
          />
        );
      case 'privacyDisclosure':
        return (
          <PrivacyDisclosureStep
            form={form}
            question={question}
            errorMessage={errorMessage}
            setStepErrorMessage={setStepErrorMessage}
          />
        );

      default:
        return null;
    }
  };

  // console.log('filteredHealthQuestions[currentStep]', filteredHealthQuestions[currentStep], currentStep)

  return (
    <Form {...form}>
      <OnboardingFormStepLayout
        handleFormSubmit={onNextClick}
        onPrev={currentStep > 0 ? onPrevClick : undefined}
        title={showAnimationScreen ? "Hold tight. We are consulting with the health plan elves..." : title}
        description={!showAnimationScreen ? description : ""}
        isFormValid={true}
        isLoading={
          showAnimationScreen ||
          isUnderwritingLoading ||
          isSubmittingApplication
        }
        isAnimationScreen={showAnimationScreen}
        backgroundClassName={showAnimationScreen ? 'bg-transparent' : undefined}
        headerAnimationKey={`health-questions-${currentStep}-${currentStepQuestion?.id ?? 'unknown'}`}
        contentAnimationKey={`health-questions-content-${currentStep}-${currentStepQuestion?.id ?? 'unknown'}`}
      >
        <div className="flex w-full rounded-2xl min-h-full max-2xl:mt-2 max-2xl:mb-6">
          <div className="flex-1 relative min-h-full">
            {showAnimationScreen ? (
              renderAnimationScreen()
            ) : (
              <AnimatePresence mode="wait">
                <motion.div
                  key={
                    defaultStep
                      ? 'coverageStartDate'
                      : currentStepQuestion?.id ?? `step-${currentStep}`
                  }
                  variants={pageTransitionVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  className="h-full"
                >
                  {defaultStep > 0 ? (
                    <CoverageStartDateStep
                      form={form}
                      question={filteredHealthQuestions[0]}
                      errorMessage={stepErrorMessage}
                      setStepErrorMessage={setStepErrorMessage}
                    />
                  ) : (renderStep())}
                </motion.div>
              </AnimatePresence>
            )}
          </div>
        </div>
      </OnboardingFormStepLayout>
    </Form>
  );
};
